import { getEnvironments } from '@/react/portainer/environments/environment.service';
import {
  getReturnUrl,
  cleanReturnUrl,
} from '@/react/portainer/helpers/returnUrl';
import { resolveLandingAddonPath } from '@/react/portainer/addons/resolveLandingAddonPath';
import { notifyError } from '@/portainer/services/notifications';

import { IAuthenticationService } from '../services/types';

export type PostLoginTarget = { state: string } | { url: string };

interface ILicenseService {
  resetState(): void;
  info(): Promise<{ valid: boolean }>;
}

interface Dependencies {
  Authentication: IAuthenticationService;
  LicenseService: ILicenseService;
}

/**
 * Decides where to send the user right after a successful login
 */
export async function resolvePostLoginTarget({
  Authentication,
  LicenseService,
}: Dependencies): Promise<PostLoginTarget> {
  // The React login mutations call the SDK directly, bypassing
  // Authentication.login()/OAuthLogin(), so the in-memory user cache can
  // still be stale here. init() re-syncs it against localStorage before we
  // read isPureAdmin()/getUserDetails() below.
  await Authentication.init();

  if (Authentication.getUserDetails().portainerAccessDenied) {
    // These users never reach the returnUrl handling below, so the add-on they
    // were last in — recorded as a returnUrl when they logged out or their
    // session expired — is consumed here instead of being discarded.
    const storedReturnUrl = getReturnUrl();
    cleanReturnUrl();
    if (storedReturnUrl) {
      return { url: storedReturnUrl };
    }

    return { url: await resolveLandingAddonPath() };
  }

  if (Authentication.isPureAdmin()) {
    return resolveAdminPostLoginTarget(Authentication, LicenseService);
  }

  return resolveUserPostLoginTarget(Authentication);
}

async function resolveAdminPostLoginTarget(
  Authentication: IAuthenticationService,
  LicenseService: ILicenseService
): Promise<PostLoginTarget> {
  const licenseTarget = await checkForLicenses(LicenseService);
  if (licenseTarget) {
    return { state: licenseTarget };
  }

  if (Authentication.getUserDetails().forceChangePassword) {
    return { state: 'portainer.account' };
  }

  const endpointsTarget = await checkForEndpoints();
  if (endpointsTarget) {
    return { state: endpointsTarget };
  }

  return resolveReturnUrlOrHome();
}

function resolveUserPostLoginTarget(
  Authentication: IAuthenticationService
): PostLoginTarget {
  if (Authentication.getUserDetails().forceChangePassword) {
    return { state: 'portainer.account' };
  }

  return resolveReturnUrlOrHome();
}

function resolveReturnUrlOrHome(): PostLoginTarget {
  const returnUrl = getReturnUrl();
  cleanReturnUrl();
  if (returnUrl) {
    return { url: returnUrl };
  }

  return { state: 'portainer.home' };
}

async function checkForLicenses(LicenseService: ILicenseService) {
  try {
    LicenseService.resetState();

    const info = await LicenseService.info();
    if (!info.valid) {
      return 'portainer.init.license';
    }
  } catch (err) {
    notifyError('Failure', err, 'Unable to retrieve licenses info');
  }

  return null;
}

async function checkForEndpoints() {
  try {
    const endpoints = await getEnvironments({
      limit: 1,
      query: { excludeSnapshots: true },
    });
    if (endpoints.value.length === 0) {
      return 'portainer.wizard';
    }
  } catch (err) {
    notifyError('Failure', err, 'Unable to retrieve environments');
  }

  return null;
}
